import { ASSET_CLASS_OPTIONS } from "../profileMapping";
import type { AssetClassId, OnboardingAnswers } from "../types";

interface StepProfileProps {
  answers: OnboardingAnswers;
  onChange: (answers: OnboardingAnswers) => void;
}

/**
 * Welcome flow step 1: what does this trader care about. Asset-class cards
 * (pre-checked from squawk channel roles) plus optional free text — the free
 * text is kept verbatim as the LLM concierge's input.
 */
export default function StepProfile({ answers, onChange }: StepProfileProps) {
  const toggleAssetClass = (id: AssetClassId) => {
    const current = answers.assetClasses;
    onChange({
      ...answers,
      assetClasses: current.includes(id)
        ? current.filter((a) => a !== id)
        : [...current, id],
    });
  };

  return (
    <div>
      <h3 className="text-base font-semibold text-gray-100">
        What do you trade?
      </h3>
      <p className="mt-1 text-sm text-gray-400">
        Pick everything that applies — we&apos;ll shape your dashboard around
        it.
      </p>

      <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-2">
        {ASSET_CLASS_OPTIONS.map((option) => {
          const selected = answers.assetClasses.includes(option.id);
          return (
            <button
              key={option.id}
              type="button"
              onClick={() => toggleAssetClass(option.id)}
              aria-pressed={selected}
              className={`rounded-lg border p-3 text-left transition-colors cursor-pointer ${
                selected
                  ? "border-squawk-blue bg-squawk-blue/10"
                  : "border-gray-700 bg-gray-900/50 hover:border-gray-500"
              }`}
            >
              <div
                className={`text-sm font-semibold ${
                  selected ? "text-squawk-blue" : "text-gray-100"
                }`}
              >
                {option.label}
              </div>
              <div className="mt-0.5 text-xs text-gray-400">{option.blurb}</div>
            </button>
          );
        })}
      </div>

      <label
        htmlFor="onboarding-free-text"
        className="mt-5 block text-sm font-medium text-gray-300"
      >
        Anything specific you want to monitor?{" "}
        <span className="font-normal text-gray-500">(optional)</span>
      </label>
      <textarea
        id="onboarding-free-text"
        value={answers.freeText ?? ""}
        onChange={(e) => onChange({ ...answers, freeText: e.target.value })}
        placeholder="e.g. US tech earnings, FOMC speakers, and anything moving crude"
        rows={3}
        maxLength={2000}
        className="mt-1.5 w-full rounded-md border border-gray-600 bg-gray-700/50 px-3 py-2 text-sm text-gray-100 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-squawk-blue focus:border-transparent resize-none"
      />

      <div className="mt-4">
        <span className="block text-sm font-medium text-gray-300">
          Used Newsquawk before?
        </span>
        <div className="mt-1.5 flex gap-2">
          {(
            [
              { value: "new_to_squawk", label: "I'm new" },
              { value: "used_before", label: "I've used it before" },
            ] as const
          ).map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() =>
                onChange({
                  ...answers,
                  experience:
                    answers.experience === opt.value ? undefined : opt.value,
                })
              }
              className={`rounded-md border px-3 py-1.5 text-sm transition-colors cursor-pointer ${
                answers.experience === opt.value
                  ? "border-squawk-blue bg-squawk-blue/10 text-squawk-blue"
                  : "border-gray-700 bg-gray-900/50 text-gray-300 hover:border-gray-500"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
